import { useSearchTopics } from '@/hooks/api/dashboard'
import useAppStore from '@/store/useAppStore'
import { Topic, TopicListFilter } from '@/types'
import { Box, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { shallow } from 'zustand/shallow'
import { LoadingButton } from '@mui/lab'
import SVG from 'react-inlinesvg'
import { PrivacyType, PrivacyTypes, TopicType, TopicTypes } from '@/utils/constants'
import colors from '@/styles/colors'
import TopicItemsGrid from '@/pages/home/molecules/TopicItemsGrid'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { SVGS } from '@/assets/svgs'

interface Props {
  filter: TopicListFilter
  searchKey: string
  onClickTopic: (topic: Topic) => void
}

export default function SearchTopicResults({ filter, searchKey, onClickTopic }: Props) {
  const [page, setPage] = useState(1)
  const [endReached, setEndReached] = useState(false)
  const [topicSearchResult, searchKeyword] = useAppStore(
    (state) => [state.search.topicSearchResult, state.search.searchKeyword],
    shallow,
  )

  const searchTopics = useSearchTopics(searchKey)
  const scrollRef = useRef<any>(null)

  useEffect(() => {
    if (searchKey.length > 0) {
      getData(1)
    }
  }, [searchKey])

  const getData = (pageNo: number = 1) => {
    searchTopics.mutate(pageNo)
    setPage(pageNo)
  }

  const getEmptySearchIcon = useMemo(() => {
    if (filter.topicType == TopicType.PATTERN) {
      return SVGS.EmptySearchPattern
    } else if (filter.topicType == TopicType.ALBUM) {
      return SVGS.EmptySearchAlbum
    } else if (filter.topicType == TopicType.GALLERY) {
      return SVGS.EmptySearchGallery
    } else if (filter.topicType == TopicType.QUILT) {
      return SVGS.EmptySearchQuilt
    }
    return SVGS.EmptySearchPattern
  }, [filter.topicType])

  useEffect(() => {
    if (page === 1 && searchTopics.isSuccess) {
      setTimeout(() => {
        if (scrollRef?.current) {
          scrollRef?.current.scrollTo({ top: 0, left: 0 })
        }
      }, 1000)
    }
  }, [page, searchTopics.isSuccess])

  useEffect(() => {
    if (searchTopics.isSuccess && searchTopics.data && !searchTopics.isError) {
      if (searchTopics.data.length < 20) {
        setEndReached(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTopics.isSuccess, searchTopics.data, searchTopics.isError, page])

  const topics = useMemo(() => {
    let list = Object.values(topicSearchResult)
    list = list.filter((i) => {
      // const topicData = TopicTypes[Number(i.topicType)]
      // const privacyData = PrivacyTypes[Number(i.visibility)]
      // return topicData && privacyData
      return filter.topicType != TopicType.ALL ? i.topicType == filter.topicType?.toString()! : true
    })
    // sort by date updated
    list.sort((a, b) => {
      if (a.dateUpdated > b.dateUpdated) return -1
      if (a.dateUpdated < b.dateUpdated) return 1
      return 0
    })
    return list
  }, [topicSearchResult, filter])

  const onClickShowMore = () => {
    if (endReached || searchTopics.isLoading || !topics.length) {
      return
    }
    getData(page + 1)
  }

  if (searchTopics.isSuccess && topics.length == 0 && page === 1) {
    return (
      <Box
        display='flex'
        flexDirection={'column'}
        alignItems={'center'}
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          pb: '200px',
          mt: '40px',
        }}
      >
        <NoDataPlaceholder
          icon={getEmptySearchIcon}
          title={`No ${TopicTypes[filter.topicType!].tabTitle.toLowerCase()} found`}
          subTitle={`It seems we can’t find any results based on your search.`}
        />
      </Box>
    )
  }

  return (
    <Box flex={1} overflow='auto' ref={scrollRef}>
      {searchTopics.isLoading && page === 1 && (
        <Box mt={1} display='flex' justifyContent={'center'}>
          <CircularProgress />
        </Box>
      )}
      <TopicItemsGrid
        showAuthorForPublic={false}
        showAuthor={true}
        topics={topics || []}
        onClickTopicItem={onClickTopic}
        onRemoved={(item) => {}}
        filter={{
          topicType: filter?.topicType || TopicType.ALL,
          privacyType: filter.privacyType || PrivacyType.ALL,
        }}
      />
      {(page > 1 || !searchTopics.isLoading) && (
        <Box m={3} display='flex' justifyContent={'center'}>
          <LoadingButton
            variant='text'
            onClick={onClickShowMore}
            loading={searchTopics.isLoading && page > 1}
            disabled={endReached}
            sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: endReached ? '#9ca3af' : colors.meshdomBlue,
              padding: '12px 24px',
              borderRadius: '12px',
              letterSpacing: '-0.01em',

              '&:hover': {
                backgroundColor: endReached ? 'transparent' : 'rgba(59, 130, 246, 0.04)',
              },

              '&:disabled': {
                color: '#9ca3af',
              },
            }}
          >
            {endReached ? '' : 'Show More'}
          </LoadingButton>
        </Box>
      )}
    </Box>
  )
}
